import React, { useState, useRef, useCallback, useEffect } from 'react';
import ReactDOM from 'react-dom';

const ActionsDropdown = ({ subscription, onExtend, onCancel, onDelete }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0, placement: 'bottom-right' });
  const buttonRef = useRef(null);
  const dropdownRef = useRef(null);

  // Removed unused subscriptionId

  const calculatePosition = useCallback(() => {
    if (!buttonRef.current) return { top: 0, left: 0, placement: 'bottom-right' };
    
    const rect = buttonRef.current.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const viewportWidth = window.innerWidth;
    
    // Default position: below and to the right
    let top = rect.bottom + 5;
    let left = rect.right - 160;
    let placement = 'bottom-right';
    
    // Check if dropdown will go off screen at bottom
    if (top + 150 > viewportHeight) {
      top = rect.top - 150;
      placement = 'top-right';
    }
    
    // Check if dropdown will go off screen at right
    if (left + 160 > viewportWidth) {
      left = rect.right - 160;
      if (left < 10) left = 10;
    }
    
    // Check if dropdown will go off screen at left
    if (left < 10) {
      left = 10;
    }
    
    return { top, left, placement };
  }, []);

  const handleToggle = (e) => {
    e.stopPropagation();
    e.preventDefault();
    
    if (!isOpen) {
      const pos = calculatePosition();
      setPosition(pos);
    }
    
    setIsOpen(!isOpen);
  };

  const handleAction = (action, e) => {
    e.stopPropagation();
    e.preventDefault();
    setIsOpen(false);
    action(subscription);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (buttonRef.current && !buttonRef.current.contains(event.target) &&
          dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Close dropdown on escape key
  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === 'Escape' && isOpen) {
        setIsOpen(false);
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen]);

  // Recalculate position on window resize or scroll
  useEffect(() => {
    if (!isOpen) return;
    
    const handleUpdate = () => {
      const pos = calculatePosition();
      setPosition(pos);
    };
    
    window.addEventListener('resize', handleUpdate);
    window.addEventListener('scroll', handleUpdate);
    return () => {
      window.removeEventListener('resize', handleUpdate);
      window.removeEventListener('scroll', handleUpdate);
    };
  }, [isOpen, calculatePosition]);

  return (
    <div className="relative inline-block">
      <button
        ref={buttonRef}
        onClick={handleToggle}
        className="flex items-center justify-center w-8 h-8 rounded-full hover:bg-gray-100 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50"
        aria-label="Subscription actions"
        aria-expanded={isOpen}
      >
        <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z" />
        </svg>
      </button>

      {isOpen && ReactDOM.createPortal(
        <div 
          ref={dropdownRef}
          className="fixed bg-white rounded-md shadow-xl border border-gray-200 z-[99999]"
          style={{
            top: position.top,
            left: position.left,
            minWidth: '160px',
            maxWidth: '200px'
          }}
        >
          <div className="py-1">
            <button
              onClick={(e) => handleAction(onExtend, e)}
              className="flex items-center w-full px-4 py-2.5 text-sm text-blue-600 hover:bg-blue-50 hover:text-blue-700 transition-colors"
            >
              <svg className="w-4 h-4 mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span>Extend</span>
            </button>
            <button
              onClick={(e) => handleAction(onCancel, e)}
              className="flex items-center w-full px-4 py-2.5 text-sm text-orange-600 hover:bg-orange-50 hover:text-orange-700 transition-colors"
            >
              <svg className="w-4 h-4 mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span>Cancel</span>
            </button>
            <button
              onClick={(e) => handleAction(onDelete, e)}
              className="flex items-center w-full px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 hover:text-red-700 transition-colors"
            >
              <svg className="w-4 h-4 mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 0 00-1-1h-4a1 0 00-1 1v3M4 7h16" />
              </svg>
              <span>Delete</span>
            </button>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default ActionsDropdown;